import { api } from './api';
import type { CollectionRow, PackageRow } from './api';

/**
 * Season tools (D-371): building a season from its schedule, publishing games
 * in bulk, the season's price grid, and a package's "every game" components.
 * Kept beside api.ts, like api-plans.ts.
 */

export interface Note {
  code: string;
  message: string;
}

/** Which games an action applies to. Every field narrows; an empty selection is every game. */
export interface GameSelection {
  event_ids?: string[];
  weekdays?: number[];
  category_ids?: string[];
  from?: string;
  to?: string;
  statuses?: ('DRAFT' | 'PUBLISHED' | 'CANCELLED')[];
}

export interface SeasonGame {
  event_id: string;
  slug: string;
  title: string;
  status: string;
  featured: number;
  opponent_id: string | null;
  opponent_name: string | null;
  /** The series it is in (D-375). */
  series_id: string | null;
  promo_title: string | null;
  performance_id: string | null;
  performance_status: string | null;
  starts_at: string | null;
  local_date: string | null;
  timezone: string | null;
  inventory_initialized_at: string | null;
  weekday: number | null;
  categories: { id: string; name: string }[];
  active_version: { id: string; version: number } | null;
  draft_version: { id: string; version: number; points: number } | null;
  missing: Note[];
}

export interface BuildRow {
  line?: number;
  date: string;
  time: string;
  opponent: string;
  promo_title?: string | null;
  promo_detail?: string | null;
  promo_sponsor?: string | null;
  categories?: string[];
  featured?: boolean;
}

export interface BuildRowPlan {
  index: number;
  line: number | null;
  date: string;
  time: string;
  weekday: number | null;
  starts_at: string | null;
  title: string | null;
  slug: string | null;
  opponent: { id: string | null; name: string; create: boolean } | null;
  promo_title: string | null;
  featured: boolean;
  categories: { id: string; name: string }[];
  status: 'CREATE' | 'EXISTS' | 'ERROR';
  problems: Note[];
  warnings: Note[];
  existing_event_id: string | null;
  event_id: string | null;
}

export interface BuildPlan {
  dry_run: boolean;
  season: { id: string; name: string; starts_on: string | null; ends_on: string | null };
  template: { id: string; title: string; slug: string; venue_id: string; timezone: string; doors_lead_minutes: number | null };
  series_id: string | null;
  title_pattern: string;
  rows: BuildRowPlan[];
  summary: { create: number; exists: number; errors: number; opponents_to_create: string[] };
  group_by_opponent?: boolean;
  created?: number;
  /** The grouping after a build, when it ran (D-375). */
  series?: { summary: SeriesPlan['summary']; series: { series_id: string | null; action: string; name: string; slug: string; games: number }[] } | null;
}

/** One series a grouping makes, extends or keeps (D-375). */
export interface SeriesChange {
  action: 'CREATE' | 'UPDATE' | 'KEEP';
  series_id: string | null;
  name: string;
  rename_from: string | null;
  slug: string;
  code: string;
  opponent: { id: string; name: string } | null;
  starts_on: string;
  ends_on: string;
  dates: string[];
  games: { event_id: string; title: string; local_date: string | null; starts_at: string | null; from_series_id: string | null }[];
  moving: number;
}

export interface SeriesPlan {
  dry_run: boolean;
  season: { id: string; name: string };
  name_pattern: string;
  regroup: boolean;
  series: SeriesChange[];
  left_out: { event_id: string; title: string; local_date: string | null; reason: 'CANCELED' | 'NO_OPPONENT' | 'NO_DATE' | 'IN_A_SERIES' | 'TOO_SHORT' }[];
  switched_off: { id: string; name: string }[];
  summary: { create: number; update: number; keep: number; games: number; left_out: number };
}

export interface GroupSeriesInput {
  name_pattern?: string;
  min_dates?: number;
  regroup: boolean;
  dry_run: boolean;
}

export interface MakeSeriesInput {
  event_ids: string[];
  name?: string;
  dry_run: boolean;
}

export interface SeriesPackageInput {
  ticket_type_code: string;
  percent_off: number;
  price_zone_code?: string;
  same_seat?: boolean;
  dry_run: boolean;
}

/** What "Create a package for this series" would make, or made (D-375). */
export interface SeriesPackagePlan {
  dry_run: boolean;
  series: { id: string; name: string; slug: string | null };
  code: string;
  name: string;
  kind: 'FIXED_PACKAGE';
  same_seat: boolean;
  seated: boolean;
  ticket_type_code: string;
  price_zone_code: string | null;
  percent_off: number;
  games: { event_id: string; title: string; starts_at: string | null; price_cents: number | null; price_from: 'ACTIVE' | 'DRAFT' | null; problem: string | null }[];
  sum_cents: number;
  price_cents: number;
  existing: { id: string; code: string; name: string; status: string } | null;
  problems: string[];
  package_id: string | null;
}

export interface BuildInput {
  template_event_id: string;
  season_id: string;
  series_id?: string | null;
  /** With no series_id: one series per run against the same team, after the build (D-375). Default true. */
  group_by_opponent?: boolean;
  /** Group once this request's games are made; false for every part but the last. */
  group_after_build?: boolean;
  title_pattern?: string;
  keep_template_categories: boolean;
  create_opponents: boolean;
  /** The schedule as rows, or as the pasted text (read by the API with the same reader). */
  rows?: BuildRow[];
  text?: string;
  dry_run: boolean;
}

export type PublishStep = 'ACTIVATE_PRICES' | 'INITIALIZE_INVENTORY' | 'PUBLISH' | 'SCHEDULE' | 'PUT_ON_SALE';

export interface GamePublishPlan {
  event_id: string;
  title: string;
  starts_at: string | null;
  status: string;
  missing: Note[];
  actions: { step: PublishStep; performance_id: string | null; message: string }[];
  blockers: Note[];
  warnings: Note[];
  result?: { ok: boolean; done: PublishStep[]; error?: Note };
}

export interface PublishInput {
  selection: GameSelection;
  steps: { activate_prices: boolean; initialize_inventory: boolean; publish: boolean; put_on_sale: boolean };
  on_sale_at: string | null;
  dry_run: boolean;
}

export interface PriceColumn {
  key: string;
  zone_code: string;
  zone_name: string;
  type_code: string;
  type_name: string;
}

export interface PriceCell {
  face_value_cents: number;
  list_price_cents: number;
}

export interface PriceGridRow {
  event_id: string;
  title: string;
  status: string;
  starts_at: string | null;
  local_date: string | null;
  timezone: string | null;
  weekday: number | null;
  categories: { id: string; name: string }[];
  version: { id: string; version: number; status: 'ACTIVE' | 'DRAFT' } | null;
  draft_in_progress: boolean;
  cells: Record<string, PriceCell | null>;
  other_prices: number;
}

export type PriceOperation = { kind: 'SET'; cents: number } | { kind: 'ADJUST'; cents: number } | { kind: 'COPY'; from_event_id: string };

export interface PriceEditInput {
  selection: GameSelection;
  columns?: string[];
  operation: PriceOperation;
  include_tiered: boolean;
  activate: boolean;
  reason?: string | null;
  dry_run: boolean;
}

export interface GamePricePlan {
  event_id: string;
  title: string;
  starts_at: string | null;
  action: 'NEW_VERSION' | 'EDIT_DRAFT' | 'UNCHANGED' | 'SKIPPED';
  from_version: { id: string; version: number; status: 'ACTIVE' | 'DRAFT' } | null;
  changes: { key: string; from: PriceCell | null; to: PriceCell }[];
  other_changes: number;
  reason: string | null;
  result?: { version_id: string; version: number; activated: boolean };
}

export interface SeasonComponentsInput {
  season_id: string;
  selection: GameSelection;
  ticket_type_code: string;
  quantity: number;
  required?: boolean;
  mode: 'APPEND' | 'REPLACE';
  dry_run: boolean;
}

export interface SeasonComponentsPlan {
  dry_run: boolean;
  adding: { event_id: string; title: string; performance_id: string | null; starts_at: string | null; ticket_type_id: string }[];
  skipped: { event_id: string; title: string; starts_at: string | null; reason: string }[];
  kept: number;
  total: number;
  package?: PackageRow;
}

export const seasonEndpoints = {
  games: (seasonId: string) =>
    api.get<{ season: CollectionRow; games: SeasonGame[]; ticket_types: { code: string; name: string; games: number }[] }>(`/api/admin/seasons/${seasonId}/games`),
  build: (input: BuildInput) => api.post<BuildPlan>('/api/admin/seasons/build', input),
  publish: (seasonId: string, input: PublishInput) =>
    api.post<{ dry_run: boolean; games: GamePublishPlan[]; missing_permissions: string[]; apply_limit?: number }>(`/api/admin/seasons/${seasonId}/publish`, input),
  prices: (seasonId: string) => api.get<{ season: CollectionRow; columns: PriceColumn[]; games: PriceGridRow[] }>(`/api/admin/seasons/${seasonId}/prices`),
  editPrices: (seasonId: string, input: PriceEditInput) => api.post<{ dry_run: boolean; columns: PriceColumn[]; games: GamePricePlan[] }>(`/api/admin/seasons/${seasonId}/prices`, input),
  packageComponents: (packageId: string, input: SeasonComponentsInput) => api.post<SeasonComponentsPlan>(`/api/admin/packages/${packageId}/components/season`, input),
  groupSeries: (seasonId: string, input: GroupSeriesInput) => api.post<SeriesPlan>(`/api/admin/seasons/${seasonId}/series/group`, input),
  makeSeries: (seasonId: string, input: MakeSeriesInput) => api.post<SeriesPlan>(`/api/admin/seasons/${seasonId}/series`, input),
  seriesPackage: (seriesId: string, input: SeriesPackageInput) => api.post<SeriesPackagePlan>(`/api/admin/packages/series/${seriesId}`, input),
};

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
